/**
 * Estado de disponibilidad de las butacas, sin DOM.
 *
 * Fuente: la foto que publica la API (`b` vendidas, `h` bloqueadas con vencimiento, `x` bloqueadas por el
 * organizador, `ga` zonas). Sobre esa base se superponen tres cosas locales:
 *   - `mine`: mi selección. Mis butacas salen en la foto como "bloqueadas"; aquí NO cuentan como ocupadas.
 *   - `overrides`: butacas que el servidor acaba de rechazar (409). Se muestran ocupadas hasta que llegue
 *     una foto generada después del conflicto.
 *   - `ghosts`: butacas que acabo de soltar. La foto aún las puede mostrar bloqueadas (la mía); se enseñan libres.
 *
 * El reloj del servidor se estima con `offset = serverTime - Date.now()`: una butaca de otro vence cuando
 * `vence < ahora + offset`. Es la misma regla que aplica el servidor al bloquear (`exp < now`).
 */
import type { Snapshot } from "./types.js";

export const FREE = 0;
export const SOLD = 1;
export const HELD = 2;
export const BLOCKED = 3;
/** Solo visual: butaca de mi selección. */
export const MINE = 4;

const GHOST_MAX_MS = 15_000;

interface Ghost {
  since: number;
  confirmedAt: number | null;
}

const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === "object" && !Array.isArray(v);

/**
 * Normaliza lo que devuelva la URL de disponibilidad. Devuelve null si no es un objeto JSON utilizable
 * (p. ej. la CDN responde con un XML de error porque la foto todavía no existe).
 */
export function parseSnapshot(raw: unknown): Snapshot | null {
  if (!isObj(raw)) return null;
  const ids = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : []);
  const h: Array<[string, number]> = [];
  if (Array.isArray(raw.h)) {
    for (const e of raw.h) if (Array.isArray(e) && typeof e[0] === "string" && typeof e[1] === "number") h.push([e[0], e[1]]);
  }
  const ga: Snapshot["ga"] = {};
  if (isObj(raw.ga)) {
    for (const zone of Object.keys(raw.ga)) {
      const g = raw.ga[zone];
      if (isObj(g)) ga[zone] = { cap: Number(g.cap) || 0, taken: Number(g.taken) || 0 };
    }
  }
  return { generatedAt: typeof raw.generatedAt === "number" ? raw.generatedAt : 0, b: ids(raw.b), h, x: ids(raw.x), ga };
}

/**
 * Foto "todo libre": se usa cuando el evento aún no tiene foto publicada (no hay ningún cambio todavía).
 * Sin `ga`, la capacidad de cada zona se toma del plano (ver `gaFree`). `generatedAt: 0` nunca bloquea
 * a una foto real posterior.
 */
export const emptySnapshot = (): Snapshot => ({ generatedAt: 0, b: [], h: [], x: [], ga: {} });

export interface ApplyResult {
  /** foto más antigua que la ya aplicada: se ignora */
  stale: boolean;
  /** butacas cuyo aspecto cambió */
  changed: number[];
  /** zonas cuyos contadores cambiaron */
  gaChanged: string[];
}

export class Availability {
  readonly base: Uint8Array;
  readonly heldUntil = new Map<number, number>();
  readonly ga = new Map<string, { cap: number; taken: number }>();
  readonly mine = new Set<number>();
  generatedAt = Number.NEGATIVE_INFINITY;
  private overrides = new Map<number, { status: number; at: number }>();
  private ghosts = new Map<number, Ghost>();

  constructor(
    private readonly index: ReadonlyMap<string, number>,
    readonly size: number,
  ) {
    this.base = new Uint8Array(size);
  }

  /** Estado visual de una butaca. */
  visual(i: number): number {
    if (this.mine.has(i)) return MINE;
    const b = this.base[i]!;
    if (b === HELD && this.ghosts.has(i)) return FREE;
    return b;
  }

  /** Foto completa -> nuevo estado. Devuelve solo lo que cambió. */
  apply(snap: Snapshot, serverNow: number): ApplyResult {
    if (snap.generatedAt < this.generatedAt) return { stale: true, changed: [], gaChanged: [] };

    const before = new Uint8Array(this.size);
    for (let i = 0; i < this.size; i++) before[i] = this.visual(i);

    const next = new Uint8Array(this.size);
    const until = new Map<number, number>();
    for (const entry of snap.h ?? []) {
      const id = entry[0];
      const exp = entry[1];
      const i = this.index.get(id);
      if (i === undefined || typeof exp !== "number") continue;
      if (exp < serverNow) continue; // bloqueo vencido = libre
      next[i] = HELD;
      until.set(i, exp);
    }
    for (const id of snap.x ?? []) {
      const i = this.index.get(id);
      if (i !== undefined) {
        next[i] = BLOCKED;
        until.delete(i);
      }
    }
    for (const id of snap.b ?? []) {
      const i = this.index.get(id);
      if (i !== undefined) {
        next[i] = SOLD;
        until.delete(i);
      }
    }

    this.overrides.forEach((o, i) => {
      if (snap.generatedAt >= o.at) this.overrides.delete(i); // la foto ya es posterior al conflicto
      else if (next[i] !== SOLD) {
        next[i] = o.status;
        until.delete(i);
      }
    });
    this.ghosts.forEach((g, i) => {
      if (next[i] !== HELD || (g.confirmedAt !== null && snap.generatedAt >= g.confirmedAt)) this.ghosts.delete(i);
    });

    this.base.set(next);
    this.heldUntil.clear();
    until.forEach((e, i) => this.heldUntil.set(i, e));
    this.generatedAt = snap.generatedAt;

    const changed: number[] = [];
    for (let i = 0; i < this.size; i++) if (this.visual(i) !== before[i]) changed.push(i);

    const gaChanged: string[] = [];
    const seen = new Set<string>();
    const gaIn = snap.ga ?? {};
    for (const zone of Object.keys(gaIn)) {
      const g = gaIn[zone];
      seen.add(zone);
      const prev = this.ga.get(zone);
      const cap = Number(g?.cap) || 0;
      const taken = Number(g?.taken) || 0;
      if (!prev || prev.cap !== cap || prev.taken !== taken) gaChanged.push(zone);
      this.ga.set(zone, { cap, taken });
    }
    for (const zone of [...this.ga.keys()]) {
      if (!seen.has(zone)) {
        this.ga.delete(zone);
        gaChanged.push(zone);
      }
    }
    return { stale: false, changed, gaChanged };
  }

  /** Paso del tiempo sin foto nueva: los bloqueos ajenos vencen y los fantasmas caducan. */
  tick(serverNow: number): number[] {
    const changed: number[] = [];
    this.heldUntil.forEach((exp, i) => {
      if (exp < serverNow) {
        const before = this.visual(i);
        this.heldUntil.delete(i);
        if (this.base[i] === HELD) this.base[i] = FREE;
        if (this.visual(i) !== before) changed.push(i);
      }
    });
    this.ghosts.forEach((g, i) => {
      if (serverNow - g.since > GHOST_MAX_MS) this.ghosts.delete(i);
    });
    return changed;
  }

  /** Marca/desmarca una butaca como mía. Devuelve true si cambió su aspecto. */
  setMine(i: number, on: boolean, serverNow: number): boolean {
    if (this.mine.has(i) === on) return false;
    const before = this.visual(i);
    if (on) {
      this.mine.add(i);
      this.ghosts.delete(i);
      this.overrides.delete(i);
    } else {
      this.mine.delete(i);
      if (this.base[i] === HELD) this.ghosts.set(i, { since: serverNow, confirmedAt: null });
    }
    return this.visual(i) !== before;
  }

  /** El servidor confirmó que solté la butaca: la foto "buena" es la generada desde ese instante. */
  confirmReleased(i: number, serverTime: number): void {
    const g = this.ghosts.get(i);
    if (g) g.confirmedAt = serverTime;
  }

  /** El servidor rechazó la butaca (409): se muestra ocupada hasta que llegue una foto posterior. */
  force(i: number, status: number, serverNow: number): boolean {
    const before = this.visual(i);
    this.mine.delete(i);
    this.ghosts.delete(i);
    this.overrides.set(i, { status, at: serverNow });
    this.base[i] = status;
    this.heldUntil.delete(i);
    return this.visual(i) !== before;
  }

  /** Cuántas entradas quedan para mí en una zona, descontando lo que ya reservé yo. */
  gaFree(zoneId: string, capacity: number, myConfirmedQty: number): number {
    const g = this.ga.get(zoneId);
    const cap = g ? g.cap : capacity;
    const taken = g ? g.taken : 0;
    const othersTaken = Math.max(0, taken - myConfirmedQty);
    return Math.max(0, cap - othersTaken);
  }
}
